'use client';

import React, { Suspense } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { LightBulbIcon, ArrowPathIcon, UserIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const Spline = React.lazy(() => import('@splinetool/react-spline/next'));

export function LifeAtGradle() {
  return (
    <section id="life-at-gradle" className="py-section bg-black text-white">
      <div className="px-section pb-section">
        <div className="max-w-section flex flex-col items-center justify-center">
          <h3 className="text-h3 text-center">Our company values</h3>

          <div className="mb-8 h-[30rem]">
            <Suspense>
              <Spline scene="https://prod.spline.design/L-cXCyHvv69aDmgD/scene.splinecode" />
            </Suspense>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {perksData.map((perk, index) => (
              <div
                key={index}
                className={clsx(
                  'border-b border-dark-background lg:last:border-b-0 lg:odd:border-r lg:odd:border-dark-background lg:[&:nth-child(3)]:border-b-0',
                )}
              >
                <PerkCard {...perk} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface PerkCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

export function PerkCard({ icon, title, description, className }: PerkCardProps) {
  return (
    <div className={clsx('flex flex-col gap-6 px-0 py-10 text-white lg:p-8', className)}>
      <div className="text-gradle-blue">{icon}</div>

      <div className="flex flex-col gap-2">
        <h3 className="text-eyebrow">{title}</h3>
        <p className="text-body text-gray-4">{description}</p>
      </div>
    </div>
  );
}

const perksData = [
  {
    icon: <MagnifyingGlassIcon className="size-10" />,
    title: 'Seek to understand',
    description:
      'Everything starts with listening and understanding, and we strive to understand different viewpoints, problems, and motivations. Before we take action, we ensure we truly grasp the challenges, perspectives, and goals.',
  },
  {
    icon: <LightBulbIcon className="size-10" />,
    title: 'Know the why',
    description:
      'We approach our work with a clear sense of purpose, ensuring every step is deliberate and focused. We take meaningful action with urgency, but never at the expense of thoughtful consideration.',
  },
  {
    icon: <UserIcon className="size-10" />,
    title: 'Own the outcome',
    description:
      'We are empowered to take initiative and we maintain transparency in our work and its outcomes. When we execute, we take responsibility for our decisions, measure the success of our innovations, and learn from the results.',
  },
  {
    icon: <ArrowPathIcon className="size-10" />,
    title: 'Innovate & iterate',
    description:
      'We embrace challenges and are not afraid to try new things, even if they might fail. With deep understanding and a clear purpose, we can develop creative and bold solutions to tackle challenges.',
  },
];
